import { Box, Table, TableBody, TableContainer, TableHead } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PAGE_SIZE } from 'services/constants/env';
import { getErrorTypesRequest } from 'redux/reducers/errorCorrections/reducer';
import { ItemWithId, TEasTableProps } from 'ui/EasTable/types';
import { EasTableHeader } from './EasTableHeader';
import { EasTableRow } from './EasTableRow';
import { COLORS } from '../../services/constants/colors';
import { EasSkeleton } from '../EasSkeleton';

const ROWS = Array.from(new Array(PAGE_SIZE / 2));

export const EasTable = <T extends ItemWithId>({
	tableStructure,
	items,
	itemsIsLoading,
	handleAction,
	handleLink,
	skeletonRows = ROWS,
	highlightedRow,
	disabledRowFn,
}: TEasTableProps<T>) => {
	const dispatch = useDispatch();
	useEffect(() => {
		dispatch(getErrorTypesRequest());
	}, [dispatch]);

	const [selectedRow, setSelectedRow] = useState<number | null>(null);

	const keyColumn = tableStructure.find((tableStructureItem) => tableStructureItem.key);

	// The key column may compose its value out of several fields (e.g. `id - processingType`) when
	// the selector alone does not identify a row uniquely — prefer that value when it is present.
	const getRowKey = (item: T): string => {
		const composedKey = keyColumn?.value?.(item);
		if (composedKey !== undefined && composedKey !== null && typeof composedKey !== 'object')
			return String(composedKey);

		const keyField = keyColumn?.selector;
		const key = keyField && typeof keyField === 'string' ? item[keyField as keyof T] : undefined;

		// A key column pointing at a field the API does not return would give every row the same
		// key, which makes React keep stale rows around — fall back to the id instead.
		return key === undefined || key === null || key === '' ? String(item.id) : String(key);
	};

	return (
		<Box>
			<TableContainer>
				<Table onMouseLeave={() => setSelectedRow(null)} onBlur={() => setSelectedRow(null)}>
					{items && items.length > 0 && (
						<TableHead>
							<EasTableHeader tableStructure={tableStructure} />
						</TableHead>
					)}
					{items && items.length > 0 && (
						<TableBody
							onMouseLeave={() => setSelectedRow(null)}
							onBlur={() => setSelectedRow(null)}
						>
							{items.map((item, index) => (
								<EasTableRow<T>
									key={getRowKey(item)}
									item={item}
									setSelectedRow={setSelectedRow}
									selectedRow={selectedRow}
									highlightedRow={highlightedRow}
									index={index}
									tableStructure={tableStructure}
									handleAction={handleAction}
									handleLink={handleLink}
									disabledRowFn={disabledRowFn}
								/>
							))}
						</TableBody>
					)}
				</Table>
				{itemsIsLoading && items && !items.length && (
					<Box
						sx={{
							pb: '1px',
						}}
					>
						{skeletonRows?.map(() => (
							<EasSkeleton key={nanoid()} />
						))}
					</Box>
				)}
				{!itemsIsLoading && items && !items.length && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: skeletonRows.length * 33 + 15,
							width: '100%',
							color: COLORS.SECONDARY,
							fontSize: '24px',
							fontWeight: 'bold',
						}}
					>
						No items found...
					</Box>
				)}
			</TableContainer>
		</Box>
	);
};
